import { Box, Skeleton, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { getProjectPath } from '@verantos-dev/core-constants';
import { VariableFamilyDefinition, type EpiVariableFamily } from '@verantos-dev/feature-epi-chat';
import { SubPageBreadcrumbHeader } from '@verantos-dev/shared-components';

import { useActiveChatContext } from '../context/ActiveChatContext';
import { NEW_CHAT_SENTINEL } from '../hooks/useStickyActiveChat';
import { useVariableDraftBreadcrumbs } from '../hooks/useVariableDraftBreadcrumbs';

/**
 * Review surface for a proposed variable GROUP (OR-6504).
 *
 * Mounted at ``/projects/:projectId/variable-group/:familyId``, opened by the
 * family card's "View details" in chat. Follows the same pattern as
 * {@link ProjectVariableDraftPage}: a route page whose subject is not a
 * persisted artifact, rendered from the live chat state.
 *
 * Why it reads chat state rather than an API: at this point NOTHING has been
 * created — approval is what creates the variables — so there is no row to fetch.
 * The whole review payload already rides the approval checkpoint (the backend's
 * ``family_gate_payload``), which is persisted and replayed, so a reload
 * reproduces this page for as long as the gate exists. That is also the right
 * lifetime: approval replaces the group with N individual variables, each with
 * its own page, and the ready-state design shows no group anywhere.
 *
 * Lookup contract: scan the replayed transcript for the checkpoint whose
 * ``family.familyId`` matches the URL. Matching on family id rather than taking
 * the latest family means a superseded proposal (the researcher refined at the
 * gate, so a second family was proposed) opens the group it actually names.
 *
 * Edge cases:
 *   - No chat mounted (direct link / refresh without ``?chat=``): empty state,
 *     mirroring the draft page.
 *   - Transcript still arriving (fresh load, or a reconnect after a gateway
 *     restart): a skeleton. The first render always has zero messages, so
 *     "no family found" must not be read as "no family exists".
 *   - No matching family once the transcript IS loaded: leave. A group is
 *     conversation-scoped, but its URL is project-scoped, and switching
 *     conversations deliberately PRESERVES the open pane route — right for a
 *     variable or cohort, which resolve from any conversation, wrong for a group.
 *     So a new session inherited the previous session's group URL and the pane
 *     answered "not found" about a group the researcher was never asking about.
 *     Routing back to project home (carrying ``?chat=``, or the conversation goes
 *     with it) puts them where they expected to be. §22.13 of the design doc.
 *   - No chat mounted, and none coming — the ``?chat=new`` sentinel (a new chat is
 *     ephemeral until the first message, so no provider mounts) or a group link
 *     opened cold. Same answer: leave. This page has nothing to show and project
 *     home always exists. A null context with a REAL ``?chat=`` id is different —
 *     the provider is mounted above this page, so a hard refresh has renders where
 *     the context is still null while the conversation is on its way.
 */
export const ProjectVariableGroupPage = () => {
  const { familyId = '', projectId = '' } = useParams<{ familyId: string; projectId: string }>();
  const ctx = useActiveChatContext();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  // Is a conversation expected to mount here? The chat context lives ABOVE this
  // page, so on a hard refresh there are renders where it is still null even
  // though `?chat=` names one — treating that as "no conversation" showed a
  // cold-link message on every reload.
  //
  // `?chat=new` is NOT such a case. It is the new-chat sentinel: a new chat is
  // ephemeral until the researcher's first message, so no id is minted and no
  // provider mounts. Reading the sentinel as "a conversation is coming" left this
  // page waiting forever on a group that new chat can never hold.
  const chatParam = searchParams.get('chat');
  const expectsChat = Boolean(chatParam) && chatParam !== NEW_CHAT_SENTINEL;

  const family: EpiVariableFamily | null = useMemo(() => {
    for (const message of ctx?.page.messages ?? []) {
      for (const part of message.parts) {
        if (part.kind !== 'checkpoint') continue;
        const candidate = part.checkpoint.family;
        if (candidate && candidate.familyId === familyId) return candidate;
      }
    }
    return null;
  }, [ctx?.page.messages, familyId]);

  // "Not found" and "not loaded yet" are different answers, and only one of them
  // is safe to state. The transcript arrives asynchronously — on a fresh page
  // load, or right after a gateway restart, the first render always has zero
  // messages — so treating an empty transcript as a missing group tells the
  // researcher their group is gone while it is still on its way.
  const page = ctx?.page;
  const stillArriving =
    // The chat context itself has not mounted yet, but the URL says one is coming.
    (ctx === null && expectsChat) ||
    // The two signals the chat pane uses for its OWN "loading session…" skeleton:
    // `initializing` while a new session's welcome streams, `subscribing` between
    // firing subscribe and the backend's ready ack (cleared by a snapshot
    // hydrate, so it is false exactly when the transcript has landed).
    //
    // An earlier version also treated "no messages and not connected" as loading,
    // which cannot tell a transcript that has not arrived from a conversation that
    // genuinely has none — so switching to a NEW chat left this page waiting
    // forever on a group that will never appear in it.
    (page !== undefined && (page.initializing || page.subscribing));

  // A group is conversation-scoped; its URL is not. Switching conversations
  // preserves the open pane route — right for a variable or cohort, which resolve
  // from any conversation, wrong for a group — so the pane kept a group URL the
  // new conversation can never resolve. Route back to project home instead.
  //
  // Gated only on the wait above, and deliberately NOT on a chat being mounted:
  // the cases with no context (the new-chat sentinel, a group link opened cold)
  // are exactly the ones where this page has nothing to show, so they belong here
  // too. Project home always exists to go back to.
  const isStale = !family && !stillArriving;
  useEffect(() => {
    if (!isStale || !projectId) return;
    // `replace` so Back does not bounce straight into the dead route again, and
    // the search string is carried so the open conversation survives the hop.
    navigate({ pathname: getProjectPath(projectId), search }, { replace: true });
  }, [isStale, navigate, projectId, search]);

  if (!family && stillArriving) {
    return (
      <Box
        data-testid="project-variable-group-page"
        sx={{ paddingX: 6, paddingY: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading this variable group…
        </Typography>
        <Skeleton variant="rectangular" height={32} />
        <Skeleton variant="rectangular" height={160} />
      </Box>
    );
  }

  // Leaving (see the effect above) — render nothing rather than flashing an
  // explanation the researcher never needed to read. Also the only remaining way
  // to be here without a family, so it narrows the render below.
  if (!family) return null;

  return (
    <ProjectVariableGroupPageShell family={family}>
      <VariableFamilyDefinition family={family} />
    </ProjectVariableGroupPageShell>
  );
};

/**
 * Page shell: breadcrumb header with the group's title and its Draft pill, then
 * the definition body. Deliberately the same chrome a single variable's page
 * wears — the group IS a variable definition, one of whose fields is a parameter
 * rather than a value, so a researcher who can read one can read the other.
 *
 * SQL and Verification render as DISABLED tabs: neither exists until the group is
 * approved and built, at which point each member owns its own. Showing them
 * disabled says that, and keeps the page recognisably a variable page.
 */
const ProjectVariableGroupPageShell = ({
  family,
  children,
}: {
  family: EpiVariableFamily;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  const { breadcrumbs } = useVariableDraftBreadcrumbs({ draftTitle: family.displayName });

  return (
    <Box
      data-testid="project-variable-group-page"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <SubPageBreadcrumbHeader
          id="project-variable-group-header"
          breadcrumbs={breadcrumbs}
          title={family.displayName}
          rightContent={
            <Box
              component="span"
              data-testid="project-variable-group-status"
              sx={{
                paddingX: 1,
                paddingY: 0.25,
                borderRadius: 1,
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Draft
            </Box>
          }
        />
      </Box>
      {/* Type and scope, worded exactly as a single variable's page words them:
          what the group produces IS variables, and claiming a different type here
          would invent an asset kind the platform does not have. The group-ness is
          carried by the chat card and by the Description's numbered value list. */}
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="project-variable-group-subheader"
        sx={{ paddingX: 6, paddingTop: 2, display: 'flex', gap: 4 }}
      >
        <Box component="span">Σ Type: Variable</Box>
        <Box component="span">Scope: Project</Box>
      </Typography>
      <Tabs
        value="definition"
        sx={{
          minHeight: 36,
          paddingX: 6,
          borderBottom: `1px solid ${theme.palette.divider}`,
          '& .MuiTab-root': {
            minHeight: 36,
            paddingY: 0.5,
            textTransform: 'uppercase',
            fontSize: theme.typography.caption.fontSize,
          },
        }}
        data-testid="project-variable-group-tabs"
      >
        {/* Definition is the only tab with anything in it: a group has no SQL and
            no verification until it is approved and built, at which point each
            member owns its own. Rendering the other two as disabled says that
            plainly — and keeps the page recognisably a variable page — where
            hiding them would leave the researcher wondering where they went. */}
        <Tab
          value="definition"
          label="Definition"
          data-testid="project-variable-group-tab-definition"
        />
        <Tab value="sql" label="SQL" disabled data-testid="project-variable-group-tab-sql" />
        <Tab
          value="verification"
          label="Verification"
          disabled
          data-testid="project-variable-group-tab-verification"
        />
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</Box>
    </Box>
  );
};
